import React, { useMemo, useState } from "react";
import { Button, TextInput } from "@carbon/react";
import {
  AiGenerate,
  ChartLine,
  CheckmarkOutline,
  Search,
  WarningAlt,
} from "@carbon/react/icons";

export const AGE_GROUPS = [
  { key: "below_9", label: "Below 9" },
  { key: "age_10_14", label: "10–14" },
  { key: "age_15_19", label: "15–19" },
  { key: "age_20_24", label: "20–24" },
  { key: "age_25_29", label: "25–29" },
  { key: "age_30_34", label: "30–34" },
  { key: "age_35_39", label: "35–39" },
  { key: "age_40_44", label: "40–44" },
  { key: "age_45_49", label: "45–49" },
  { key: "age_50_plus", label: "50+" },
];

export const toNumber = value => Number.parseInt(value, 10) || 0;

const percentage = (value, total) => total > 0 ? (value / total) * 100 : 0;

export const buildDashboardAnalysis = data => {
  const total = toNumber(data?.total_cases_reported);
  const completed = toNumber(data?.total_cases_received);
  const female = toNumber(data?.total_female);
  const male = toNumber(data?.total_male);
  const health = toNumber(data?.total_health_center_cases);
  const police = toNumber(data?.total_police_station_cases);
  const ages = AGE_GROUPS.map(group => ({
    ...group,
    value: toNumber(data?.[group.key]),
  }));
  const knownAgeTotal = ages.reduce((sum, group) => sum + group.value, 0);
  const largestAgeGroup = ages.reduce(
    (largest, group) => group.value > largest.value ? group : largest,
    ages[0]
  );

  return {
    total,
    completed,
    female,
    male,
    health,
    police,
    ages,
    knownAgeTotal,
    missingAge: Math.max(total - knownAgeTotal, 0),
    completionRate: percentage(completed, total),
    femaleShare: percentage(female, female + male),
    maleShare: percentage(male, female + male),
    healthShare: percentage(health, health + police),
    policeShare: percentage(police, health + police),
    largestAgeGroup,
  };
};

const formatNumber = value => value.toLocaleString();
const formatPercent = value => `${value.toFixed(1)}%`;

export const answerDashboardQuestion = (question, analysis, district) => {
  const normalized = question.trim().toLowerCase();
  const scope = district?.toUpperCase() === "ALL" ? "all districts" : district;

  if (!normalized) {
    return "Ask about completion, gender, age groups, reporting channels, or data quality.";
  }

  if (normalized.includes("complete") || normalized.includes("completion") || normalized.includes("received")) {
    return `${formatNumber(analysis.completed)} of ${formatNumber(analysis.total)} reported cases are counted as completed (${formatPercent(analysis.completionRate)}) for ${scope}.`;
  }

  if (normalized.includes("female") || normalized.includes("male") || normalized.includes("gender")) {
    return `The data contains ${formatNumber(analysis.female)} female records (${formatPercent(analysis.femaleShare)}) and ${formatNumber(analysis.male)} male records (${formatPercent(analysis.maleShare)}) for ${scope}.`;
  }

  if (normalized.includes("health") || normalized.includes("police") || normalized.includes("channel")) {
    return `Health centers account for ${formatNumber(analysis.health)} cases (${formatPercent(analysis.healthShare)}), while police stations account for ${formatNumber(analysis.police)} (${formatPercent(analysis.policeShare)}) for ${scope}.`;
  }

  if (normalized.includes("age") || normalized.includes("young")) {
    return `${analysis.largestAgeGroup.label} is the largest returned age group with ${formatNumber(analysis.largestAgeGroup.value)} records. The age fields account for ${formatNumber(analysis.knownAgeTotal)} of ${formatNumber(analysis.total)} cases.`;
  }

  if (normalized.includes("missing") || normalized.includes("quality") || normalized.includes("incomplete")) {
    return analysis.missingAge > 0
      ? `${formatNumber(analysis.missingAge)} reported cases are not represented in the returned age-group totals. Review missing or unmapped age values before age-based planning.`
      : "The returned age-group totals reconcile with the reported-case total for the current selection.";
  }

  return `For ${scope}, ${formatNumber(analysis.total)} cases are reported and ${formatNumber(analysis.completed)} are counted as completed. Try asking “What is the completion rate?” or “Which age group is largest?”`;
};

const insightViews = [
  { id: "summary", label: "Summary", icon: AiGenerate },
  { id: "quality", label: "Data quality", icon: WarningAlt },
  { id: "planning", label: "Planning", icon: ChartLine },
];

const DashboardInsights = ({ dashboardData, district }) => {
  const analysis = useMemo(
    () => buildDashboardAnalysis(dashboardData),
    [dashboardData]
  );
  const [activeView, setActiveView] = useState("summary");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(
    "Ask a question about the aggregate dashboard data. No survivor-level information is used."
  );

  const askQuestion = event => {
    event.preventDefault();
    setAnswer(answerDashboardQuestion(question, analysis, district));
  };

  const handleSuggestedQuestion = suggestedQuestion => {
    setQuestion(suggestedQuestion);
    setAnswer(answerDashboardQuestion(suggestedQuestion, analysis, district));
  };

  return (
    <section className="ai-workspace" aria-labelledby="ai-insights-title">
      <div className="section-heading ai-heading">
        <div>
          <span className="section-kicker">Privacy-safe aggregate analysis</span>
          <h2 id="ai-insights-title">AI-assisted insights</h2>
          <p>Instant summaries and data checks generated from the active dashboard filters.</p>
        </div>
        <span className="ai-status"><AiGenerate size={16} /> AI ready</span>
      </div>

      <div className="ai-layout">
        <div className="ai-insights-panel">
          <div className="ai-tabs" role="tablist" aria-label="AI insight categories">
            {insightViews.map(view => {
              const Icon = view.icon;
              return (
                <button
                  type="button"
                  className={`ai-tab ${activeView === view.id ? "is-active" : ""}`}
                  key={view.id}
                  role="tab"
                  aria-selected={activeView === view.id}
                  onClick={() => setActiveView(view.id)}
                >
                  <Icon size={16} />
                  {view.label}
                </button>
              );
            })}
          </div>

          <div className="ai-tab-content">
            {activeView === "summary" && (
              <div className="insight-list">
                <article className="insight-item">
                  <CheckmarkOutline size={20} />
                  <div><strong>Case completion</strong><p>{formatNumber(analysis.completed)} cases are counted as completed, representing {formatPercent(analysis.completionRate)} of reports.</p></div>
                </article>
                <article className="insight-item">
                  <ChartLine size={20} />
                  <div><strong>Largest returned age group</strong><p>Ages {analysis.largestAgeGroup.label} have the highest count at {formatNumber(analysis.largestAgeGroup.value)} records.</p></div>
                </article>
                <article className="insight-item">
                  <AiGenerate size={20} />
                  <div><strong>Reporting channel mix</strong><p>Health centers account for {formatPercent(analysis.healthShare)} of cases recorded by the two reporting channels.</p></div>
                </article>
              </div>
            )}

            {activeView === "quality" && (
              <div className="insight-list">
                <article className={`insight-item ${analysis.missingAge > 0 ? "needs-attention" : ""}`}>
                  <WarningAlt size={20} />
                  <div><strong>Age-field reconciliation</strong><p>{analysis.missingAge > 0 ? `${formatNumber(analysis.missingAge)} cases are not represented in the returned age-group totals.` : "Age-group totals reconcile with reported cases."}</p></div>
                </article>
                <article className="insight-item">
                  <CheckmarkOutline size={20} />
                  <div><strong>Gender-field reconciliation</strong><p>{formatNumber(analysis.female + analysis.male)} gender records are represented against {formatNumber(analysis.total)} reported cases.</p></div>
                </article>
                <article className="insight-item">
                  <AiGenerate size={20} />
                  <div><strong>Recommended check</strong><p>Review missing values, station-type mismatches, and duplicate facility names before exporting reports.</p></div>
                </article>
              </div>
            )}

            {activeView === "planning" && (
              <div className="insight-list">
                <article className="insight-item">
                  <ChartLine size={20} />
                  <div><strong>Service planning signal</strong><p>Health facilities carry {formatPercent(analysis.healthShare)} of the recorded reporting-channel workload.</p></div>
                </article>
                <article className="insight-item">
                  <WarningAlt size={20} />
                  <div><strong>Hotspot detection readiness</strong><p>Add district-by-week history to identify statistically unusual increases instead of simply ranking large districts.</p></div>
                </article>
                <article className="insight-item">
                  <WarningAlt size={20} />
                  <div><strong>Forecast readiness</strong><p>Weekly service counts and reporting-completeness indicators are required for reliable workload forecasts.</p></div>
                </article>
              </div>
            )}
          </div>
        </div>

        <div className="ask-dashboard-panel">
          <div className="ask-heading">
            <span className="ask-icon"><Search size={20} /></span>
            <div><h3>Ask the dashboard</h3><p>Query the current aggregate view in plain language.</p></div>
          </div>
          <form onSubmit={askQuestion} className="ask-form">
            <TextInput
              id="dashboard-question"
              labelText="Your question"
              hideLabel
              placeholder="What is the completion rate?"
              value={question}
              onChange={event => setQuestion(event.target.value)}
            />
            <Button type="submit" renderIcon={AiGenerate}>Ask</Button>
          </form>
          <div className="question-suggestions" aria-label="Suggested questions">
            {["Which age group is largest?", "Check data quality", "Compare reporting channels"].map(suggestion => (
              <button type="button" key={suggestion} onClick={() => handleSuggestedQuestion(suggestion)}>{suggestion}</button>
            ))}
          </div>
          <div className="ai-answer" aria-live="polite">
            <span>Answer</span>
            <p>{answer}</p>
          </div>
          <p className="ai-guardrail">Uses aggregate counts only. Outputs support planning and must not be used to automate survivor-level or policing decisions.</p>
        </div>
      </div>
    </section>
  );
};

export default DashboardInsights;
